/**
 * Iter 204d — Daily / Weekly slate panel for the Asian Handicap and
 * Goal Timing markets.  Mirrors the DoubleChanceBetslipPanel design:
 *
 *  - Semantic theme tokens (bg-background / bg-card / text-foreground / etc)
 *    so the panel responds correctly to the global day ⇄ night toggle.
 *  - 3-column responsive grid of picks instead of a dense list.
 *  - Each pick reads as a plain-English bet ("Bet: Laredo Heat -1.25"),
 *    not a string of opaque labels.  The model tier (A+/A/B+/B/C) is
 *    a secondary chip, not the headline.
 *
 *  variant: "ah" | "goal_timing"
 */
import { useEffect, useMemo, useState } from "react";
import axios from "axios";
import {
  ShieldCheck, Clock as ClockIcon, TrendingUp,
  CheckCircle2, XCircle, MinusCircle,
} from "lucide-react";
import { API } from "../App";
import { appTodayIso } from "../lib/appTime";

// ── Tier styling (theme-token-aware) ─────────────────────────────────────

const TIER_TONE = {
  "A+": "text-emerald-300 border-emerald-500/40 bg-emerald-500/10",
  "A":  "text-emerald-300 border-emerald-500/30 bg-emerald-500/5",
  "B+": "text-amber-300 border-amber-500/40 bg-amber-500/10",
  "B":  "text-amber-300 border-amber-500/30 bg-amber-500/5",
  "C":  "text-muted-foreground border-border bg-card/70",
  "NO BET": "text-rose-300 border-rose-500/30 bg-rose-500/5",
};

const TIER_LABEL = {
  "A+": "Strong",
  "A":  "Solid",
  "B+": "Decent",
  "B":  "Lean",
  "C":  "Soft",
  "NO BET": "Skip",
};


// ── Outcome chip (Iter 204e — hit tracking) ──────────────────────────────

function OutcomeChip({ outcome }) {
  if (!outcome || outcome === "pending") {
    return (
      <span className="inline-flex items-center gap-1 text-[9px] font-bold uppercase tracking-[0.14em] px-1.5 py-0.5 rounded border text-amber-300 border-amber-500/30 bg-amber-500/5">
        <ClockIcon className="w-2.5 h-2.5" /> Pending
      </span>
    );
  }
  const cfg = outcome === "hit"
    ? { Icon: CheckCircle2, cls: "text-emerald-300 border-emerald-500/40 bg-emerald-500/10", label: "Hit" }
    : outcome === "miss"
    ? { Icon: XCircle, cls: "text-rose-300 border-rose-500/40 bg-rose-500/10", label: "Miss" }
    : outcome === "half_won"
    ? { Icon: CheckCircle2, cls: "text-emerald-300 border-emerald-500/30 bg-emerald-500/5", label: "½ Hit" }
    : outcome === "half_lost"
    ? { Icon: XCircle, cls: "text-rose-300 border-rose-500/30 bg-rose-500/5", label: "½ Miss" }
    : { Icon: MinusCircle, cls: "text-muted-foreground border-border bg-card/50", label: "Push" };
  const { Icon, cls, label } = cfg;
  return (
    <span className={`inline-flex items-center gap-1 text-[9px] font-bold uppercase tracking-[0.14em] px-1.5 py-0.5 rounded border ${cls}`}>
      <Icon className="w-2.5 h-2.5" />{label}
    </span>
  );
}


// ── Plain-English bet builders ───────────────────────────────────────────

/**
 * Convert an AH headline pick into a one-line plain English bet.
 *
 *  side="home" + line=-1.25 →  "Manchester City -1.25"
 *  side="away" + line=+1.25 →  "Liverpool +1.25"
 *  side="home" + line=0     →  "Manchester City (Draw No Bet)"
 */
function buildAHBetLabel(pick) {
  const hp = pick.headline_pick || {};
  const line = hp.line;
  const side = hp.side;
  if (line == null || !side) return "—";
  const team = side === "home"
    ? (pick.home_team?.name || "Home")
    : (pick.away_team?.name || "Away");
  if (line === 0 || line === 0.0) return `${team} (DNB)`;
  const sign = line > 0 ? "+" : "";
  return `${team} ${sign}${line}`;
}

function buildAHRationale(pick) {
  const xgd = pick.expected_goal_difference;
  const margin = pick.most_likely_margin;
  const marginCopy = {
    lose_by_2_plus: "lose by 2+",
    lose_by_1:      "lose by 1",
    draw:           "draw",
    win_by_1:       "win by 1",
    win_by_2:       "win by 2",
    win_by_3_plus:  "win by 3+",
  }[margin] || margin;
  const direction = xgd > 0 ? "home advantage" : xgd < 0 ? "away advantage" : "level";
  return `xGD ${xgd > 0 ? "+" : ""}${xgd?.toFixed?.(2) ?? xgd} · ${direction} · most likely ${marginCopy}`;
}


/**
 * Convert a Goal Timing headline pick into a plain English bet.
 *
 *  "Over 0.5 HT" + "Over"      → "Over 0.5 goals (1st half)"
 *  "Over 0.5 SH" + "Over"      → "Over 0.5 goals (2nd half)"
 *  "Over 1.5 HT" + "Over"      → "Over 1.5 goals (1st half)"
 *  "Home scores SH" + "Yes"    → "<Home> to score in 2nd half"
 *  "HT Result" + "Home"        → "<Home> ahead at half-time"
 *  "HT Result" + "Draw"        → "Draw at half-time"
 *  "BTTS HT" + "Yes"           → "Both teams to score in 1st half"
 *  "Late goal 76+" + "Yes"     → "Goal after the 76th minute"
 *  "Goal before 30'" + "Yes"   → "Goal in the first 30 minutes"
 *  fallback                    → "<market> · <selection>"
 */
function buildGTBetLabel(pick) {
  const hp = pick.headline_pick || {};
  const m = (hp.market || "").trim();
  const sel = (hp.selection || "").trim();
  if (!m) return "—";
  const home = pick.home_team?.name || "Home";
  const away = pick.away_team?.name || "Away";

  const mLow = m.toLowerCase();

  if (mLow.startsWith("over ") && (mLow.endsWith(" ht") || mLow.endsWith(" sh"))) {
    const half = mLow.endsWith(" ht") ? "1st half" : "2nd half";
    // m looks like "Over 0.5 HT"
    const totals = m.replace(/ ht$/i, "").replace(/ sh$/i, ""); // "Over 0.5"
    const verb = sel.toLowerCase() === "under" ? totals.replace(/^over/i, "Under") : totals;
    return `${verb} goals (${half})`;
  }
  if (mLow.startsWith("under ") && (mLow.endsWith(" ht") || mLow.endsWith(" sh"))) {
    const half = mLow.endsWith(" ht") ? "1st half" : "2nd half";
    const totals = m.replace(/ ht$/i, "").replace(/ sh$/i, "");
    return `${totals} goals (${half})`;
  }
  if (mLow === "home scores sh") return `${home} to score in 2nd half`;
  if (mLow === "home scores ht") return `${home} to score in 1st half`;
  if (mLow === "away scores sh") return `${away} to score in 2nd half`;
  if (mLow === "away scores ht") return `${away} to score in 1st half`;
  if (mLow === "ht result") {
    if (sel.toLowerCase() === "home") return `${home} ahead at half-time`;
    if (sel.toLowerCase() === "away") return `${away} ahead at half-time`;
    if (sel.toLowerCase() === "draw") return `Draw at half-time`;
  }
  if (mLow.startsWith("btts")) {
    const half = mLow.endsWith(" ht") ? "in 1st half" : mLow.endsWith(" sh") ? "in 2nd half" : "";
    return `Both teams to score${half ? " " + half : ""}`;
  }
  if (mLow.startsWith("late goal") || mLow.includes("76+")) {
    return `Goal after the 76th minute`;
  }
  if (mLow.startsWith("goal before")) {
    return `Goal in the ${m.replace(/^goal before /i, "first ").replace("'", "")} minutes`;
  }
  if (mLow === "highest-scoring half") {
    return sel.toLowerCase() === "first" || sel.toLowerCase() === "ht"
      ? "More goals in 1st half"
      : "More goals in 2nd half";
  }
  return `${m} · ${sel}`;
}

function buildGTRationale(pick) {
  const fp = pick.headline_pick?.fair_probability;
  const fpPct = fp != null ? `${Math.round(fp * 100)}%` : "—";
  const hot = pick.most_dangerous_quarter ? `hot zone ${pick.most_dangerous_quarter}` : null;
  const half = pick.highest_scoring_half
    ? `${pick.highest_scoring_half === "first" ? "1st" : "2nd"} half edge`
    : null;
  return [`model probability ${fpPct}`, hot, half].filter(Boolean).join(" · ");
}


// ── Formatting helpers ───────────────────────────────────────────────────

const fmtDate = (iso) => {
  if (!iso) return "—";
  try {
    return new Date(iso + "T12:00:00Z").toLocaleDateString(undefined, {
      weekday: "short", month: "short", day: "numeric",
    });
  } catch { return iso; }
};

const fmtKickoff = (iso) => {
  if (!iso) return "";
  try {
    return new Date(iso).toLocaleTimeString(undefined, {
      hour: "2-digit", minute: "2-digit",
    });
  } catch { return ""; }
};


// ── Main component ───────────────────────────────────────────────────────

export default function DailyValueSlateCard({ variant = "ah", testidPrefix }) {
  const today = useMemo(() => appTodayIso(), []);
  const [day, setDay] = useState(today);
  const [week, setWeek] = useState([]);
  const [slate, setSlate] = useState(null);
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(false);

  const basePath = variant === "ah" ? "asian-handicap" : "goal-timing";
  const idPrefix = testidPrefix || (variant === "ah" ? "ah-slate" : "gt-slate");
  const title = variant === "ah" ? "Daily 25 · Asian Handicap" : "Daily 25 · Goal Timing";
  const subtitle = variant === "ah"
    ? "Ranked by goal-difference magnitude × headline-line confidence."
    : "Ranked by headline-market probability × quarter-distribution sharpness.";
  const buildBetLabel = variant === "ah" ? buildAHBetLabel : buildGTBetLabel;
  const buildRationale = variant === "ah" ? buildAHRationale : buildGTRationale;

  // Fetch week strip.
  useEffect(() => {
    let cancel = false;
    (async () => {
      try {
        const r = await axios.get(`${API}/${basePath}/week`);
        if (!cancel) setWeek(r.data?.days || []);
      } catch { /* swallow */ }
    })();
    return () => { cancel = true; };
  }, [basePath]);

  // Fetch slate for the selected day.
  useEffect(() => {
    let cancel = false;
    (async () => {
      setLoading(true);
      try {
        const r = await axios.get(`${API}/${basePath}/day/${day}`);
        if (!cancel) { setSlate(r.data); setError(null); }
      } catch (e) {
        if (!cancel) setError(e?.response?.data?.detail || e.message || "Failed");
      } finally { if (!cancel) setLoading(false); }
    })();
    return () => { cancel = true; };
  }, [day, basePath]);

  return (
    <div className="space-y-5" data-testid={`${idPrefix}-card`}>
      {/* Hero summary */}
      <div className="rounded-2xl border border-emerald-500/30 bg-gradient-to-br from-emerald-500/10 via-background to-background p-5 sm:p-6">
        <span className="inline-flex items-center gap-1.5 text-[10px] uppercase tracking-[0.22em] font-bold text-emerald-300/90">
          <ShieldCheck className="w-3 h-3" />
          {title}
        </span>
        <h2 className="font-display text-2xl sm:text-3xl font-bold mt-1.5" data-testid={`${idPrefix}-title`}>
          25 strongest {variant === "ah" ? "AH" : "Goal Timing"} picks · {fmtDate(day)}
        </h2>
        <p className="text-xs text-muted-foreground mt-1.5 max-w-xl">
          {subtitle} Each row reads as a plain bet; the small chip on the right is the model&apos;s analytical
          tier (Strong → Skip) — that&apos;s our confidence, not a recommendation to stake blind.
        </p>
        {slate && (
          <div className="text-[10px] text-muted-foreground mt-4 font-mono"
               data-testid={`${idPrefix}-meta`}>
            {slate.n_picks ?? 0} picks · engine {slate.engine_version || "—"}
            {slate.settlement_summary && (
              <span className="ml-2 text-emerald-300">
                · {slate.settlement_summary.hit ?? 0} hit · {slate.settlement_summary.miss ?? 0} miss
                {slate.settlement_summary.hit_rate != null && (
                  <> · {slate.settlement_summary.hit_rate}% hit rate</>
                )}
                {slate.settlement_summary.pending > 0 && (
                  <span className="text-amber-300"> · {slate.settlement_summary.pending} pending</span>
                )}
              </span>
            )}
          </div>
        )}
      </div>

      {/* Week strip — day chips */}
      {week.length > 0 && (
        <div className="flex items-center gap-2 overflow-x-auto pb-1" data-testid={`${idPrefix}-week`}>
          {week.map((d) => {
            const isActive = day === d.date;
            const isToday = d.date === today;
            return (
              <button
                key={d.date}
                type="button"
                onClick={() => setDay(d.date)}
                data-testid={`${idPrefix}-day-${d.date}`}
                className={`shrink-0 px-3 py-2 rounded-xl border text-left transition-colors ${
                  isActive
                    ? "border-foreground bg-foreground text-background"
                    : "border-border bg-card/40 hover:bg-card/70"
                }`}
              >
                <div className="text-[9px] uppercase tracking-[0.16em] font-bold opacity-80">
                  {fmtDate(d.date)}{isToday && " · Today"}
                </div>
                <div className="text-[11px] font-bold tabular-nums mt-0.5">
                  {d.n_picks > 0 ? `${d.n_picks} picks` : "0 picks"}
                </div>
              </button>
            );
          })}
        </div>
      )}

      {/* Picks grid */}
      {error ? (
        <div className="rounded-xl border border-rose-500/40 bg-rose-500/5 text-rose-300 text-sm p-4"
             data-testid={`${idPrefix}-error`}>
          {error}
        </div>
      ) : loading ? (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3"
             data-testid={`${idPrefix}-loading`}>
          {Array.from({ length: 6 }).map((_, i) => (
            <div key={i} className="h-32 rounded-xl border border-border bg-card/30 animate-pulse" />
          ))}
        </div>
      ) : !slate || slate.n_picks === 0 ? (
        <div className="rounded-xl border border-dashed border-border bg-card/20 p-10 text-center"
             data-testid={`${idPrefix}-empty`}>
          <ShieldCheck className="w-10 h-10 mx-auto mb-3 text-muted-foreground/40" />
          <p className="text-sm text-muted-foreground">
            No fixtures with sufficient model data for {fmtDate(day)} yet.
          </p>
        </div>
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3"
             data-testid={`${idPrefix}-list`}>
          {slate.picks.map((p, i) => {
            const tier = p.risk_tier || "C";
            const tone = TIER_TONE[tier] || TIER_TONE.C;
            const tierLabel = TIER_LABEL[tier] || tier;
            const bet = buildBetLabel(p);
            const rationale = buildRationale(p);
            return (
              <article key={`${p.fixture_id}-${i}`}
                       data-testid={`${idPrefix}-row-${i}`}
                       className="rounded-xl border border-border bg-card/50 p-3.5 hover:bg-card/70 hover:border-foreground/30 transition-colors">
                {/* League + kickoff */}
                <div className="flex items-center justify-between mb-2 text-[10px] uppercase tracking-[0.16em] text-muted-foreground">
                  <span className="truncate flex-1">{p.league?.name || "—"}</span>
                  <span className="inline-flex items-center gap-1 shrink-0">
                    <ClockIcon className="w-3 h-3" />
                    {fmtKickoff(p.kickoff)}
                  </span>
                </div>
                {/* Teams */}
                <div className="flex items-center justify-between gap-2 mb-3 min-w-0">
                  <span className="font-semibold truncate flex-1 text-sm">{p.home_team?.name || "Home"}</span>
                  <span className="text-[10px] uppercase text-muted-foreground tracking-wider shrink-0">vs</span>
                  <span className="font-semibold truncate flex-1 text-right text-sm">{p.away_team?.name || "Away"}</span>
                </div>
                {/* Bet headline */}
                <div className="rounded-lg border border-border/50 bg-background/40 px-3 py-2.5 mb-2">
                  <div className="flex items-center justify-between gap-2 mb-1.5">
                    <span className="text-[9px] uppercase tracking-[0.18em] font-bold text-emerald-300/80">
                      {variant === "ah" ? "Asian Handicap" : "Goal Timing"}
                    </span>
                    <div className="flex items-center gap-1.5">
                      <OutcomeChip outcome={p.outcome} />
                      <span className={`inline-flex items-center gap-1 text-[9px] font-bold uppercase tracking-[0.14em] px-1.5 py-0.5 rounded border ${tone}`}
                            data-testid={`${idPrefix}-row-${i}-tier`}>
                        {tier} · {tierLabel}
                      </span>
                    </div>
                  </div>
                  <div className="flex items-baseline justify-between gap-2">
                    <span className="font-display font-bold truncate text-base"
                          data-testid={`${idPrefix}-row-${i}-bet`}>
                      {bet}
                    </span>
                    <span className="font-bold tabular-nums text-emerald-300 text-base shrink-0"
                          data-testid={`${idPrefix}-row-${i}-conf`}>
                      {p.confidence_pct != null ? `${Math.round(p.confidence_pct)}%` : "—"}
                    </span>
                  </div>
                  <div className="text-[10px] text-muted-foreground mt-1 truncate">
                    {rationale}
                  </div>
                  {p.actual_score && (
                    <div className="text-[10px] text-muted-foreground mt-1 font-mono"
                         data-testid={`${idPrefix}-row-${i}-score`}>
                      FT {p.actual_score}
                    </div>
                  )}
                </div>
                <div className="flex items-center justify-between text-[10px] text-muted-foreground">
                  <span className="inline-flex items-center gap-1">
                    <TrendingUp className="w-3 h-3" />
                    Rank #{i + 1}
                  </span>
                  <span className="font-mono">
                    {variant === "ah"
                      ? `λ ${p.lambda_home} vs ${p.lambda_away}`
                      : `½ ${p.first_half_expected_goals} | ${p.second_half_expected_goals}`}
                  </span>
                </div>
              </article>
            );
          })}
        </div>
      )}
    </div>
  );
}
